<template>
  <div class="personnel-info-view">
    <!-- Loading State -->
    <div class="loading-container" v-if="loading">
      <div class="spinner"></div>
      <p>Chargement...</p>
    </div>

    <!-- Error Message -->
    <div class="error-message" v-if="error && !loading">
      <span class="material-icons">error_outline</span>
      <p>{{ error }}</p>
    </div>

    <!-- Personnel Info -->
    <div class="personnel-content" v-if="!loading && !error && agent">
      <div class="photo-section">
        <img 
          :src="getAgentPhoto()" 
          alt="Photo agent" 
          class="agent-photo"
          @error="handlePhotoError"
        />
        <div v-if="photoError" class="photo-placeholder">
          <span class="material-icons">person</span>
        </div>
      </div>

      <div class="info-section">
        <h1>{{ agent.nom }} {{ agent.prenom }}</h1>
        
        <div class="info-list">
          <div class="info-item">
            <span class="info-label">Matricule:</span>
            <span class="info-value">{{ agent.matricule || 'Non spécifié' }}</span>
          </div>
          
          <div class="info-item" v-if="agent.sexe">
            <span class="info-label">Sexe:</span>
            <span class="info-value">{{ agent.sexe === 'M' ? 'Masculin' : 'Féminin' }}</span>
          </div>
          
          <div class="info-item" v-if="agent.date_naissance">
            <span class="info-label">Date de naissance:</span>
            <span class="info-value">{{ formatDate(agent.date_naissance) }}</span>
          </div>
          
          <div class="info-item">
            <span class="info-label">Direction:</span>
            <span class="info-value">{{ getDirectionName() }}</span>
          </div>
          
          <div class="info-item">
            <span class="info-label">Fonction:</span>
            <span class="info-value">{{ getFonctionName() }}</span>
          </div>
          
          <div class="info-item">
            <span class="info-label">Grade:</span>
            <span class="info-value">{{ getGradeName() }}</span>
          </div>
          
          <div class="info-item" v-if="agent.email">
            <span class="info-label">Email:</span>
            <span class="info-value">{{ agent.email }}</span>
          </div>
          
          <div class="info-item" v-if="agent.telephone">
            <span class="info-label">Téléphone:</span>
            <span class="info-value">{{ agent.telephone }}</span>
          </div>
          
          <div class="info-item" v-if="agent.adresse">
            <span class="info-label">Adresse:</span>
            <span class="info-value">{{ agent.adresse }}</span>
          </div>

          <div class="info-block-title">Informations sociales</div>
          <div class="info-item">
            <span class="info-label">État civil:</span>
            <span class="info-value">{{ getEtatCivilLabel(agent.etat_civil) || 'Non spécifié' }}</span>
          </div>
          <template v-if="agent.etat_civil === 'marie'">
            <div class="info-item">
              <span class="info-label">Nom du conjoint:</span>
              <span class="info-value">{{ agent.nom_conjoint || 'Non spécifié' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Contact du conjoint:</span>
              <span class="info-value">{{ agent.contact_conjoint || 'Non spécifié' }}</span>
            </div>
          </template>
          <div class="info-item">
            <span class="info-label">Nombre d'enfants:</span>
            <span class="info-value">{{ agent.nombre_enfants ?? '0' }}</span>
          </div>
          <div class="info-item" v-if="getNomsEnfantsList(agent).length > 0">
            <span class="info-label">Noms des enfants:</span>
            <span class="info-value">
              <ul class="noms-enfants-list">
                <li v-for="(nom, idx) in getNomsEnfantsList(agent)" :key="idx">{{ nom }}</li>
              </ul>
            </span>
          </div>
          <div class="info-item" v-if="agent.contact_urgence">
            <span class="info-label">Contact d'urgence:</span>
            <span class="info-value">{{ agent.contact_urgence }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import axios from 'axios'

const route = useRoute()

// Component state
const loading = ref(true)
const error = ref(null)
const agent = ref(null)
const photoError = ref(false)

// API publique QR (sans authentification)
// On garde la même base URL que le backend configuré pour l'app
const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:3003'

// Methods
const fetchAgentData = async () => {
  const agentId = route.params.id
  if (!agentId) {
    error.value = "ID de l'agent non spécifié"
    loading.value = false
    return
  }

  try {
    loading.value = true
    error.value = null

    // Appel public QR (sans token)
    // Endpoint public QR (sans token)
    const response = await axios.get(`${API_URL}/api/personnel/qr/${encodeURIComponent(agentId)}`)

    if (!response?.data) {
      throw new Error("Aucune réponse de l'API")
    }

    // Gérer différentes structures de réponse possibles
    if (response.data.data) {
      agent.value = response.data.data
    } else {
      agent.value = response.data
    }
  } catch (err) {
    console.error("Erreur complète lors du chargement de l'agent:", err)
    console.error("Détails de l'erreur:", {
      message: err.message,
      status: err.response?.status,
      statusText: err.response?.statusText,
      data: err.response?.data,
      url: err.config?.url
    })
    
    if (err.response) {
      if (err.response.status === 404) {
        error.value = `Agent avec l'ID "${agentId}" non trouvé`
      } else if (err.response.status === 403) {
        error.value = "Accès interdit"
      } else {
        const errorMsg = err.response.data?.message || err.response.statusText || 'Erreur inconnue'
        error.value = `Erreur ${err.response.status}: ${errorMsg}`
      }
    } else if (err.request) {
      error.value = `Impossible de se connecter au serveur (${API_URL}). Vérifiez votre connexion.`
    } else {
      error.value = `Erreur: ${err.message || 'Impossible de charger les données de l\'agent'}`
    }
  } finally {
    loading.value = false
  }
}


const getAgentPhoto = () => {
  const photoCandidate =
    agent.value?.photo_url ||
    agent.value?.photoUrl ||
    agent.value?.photo ||
    agent.value?.avatar_url ||
    agent.value?.avatarUrl

  if (!photoCandidate) {
    return require('@/assets/images/user-default.png')
  }
  
  const photoPath = photoCandidate
  const uploadsBase = `${API_URL}/data/uploads/`
  
  // Si c'est une chaîne base64
  if (typeof photoPath === 'string' && photoPath.startsWith('data:image')) {
    return photoPath
  }
  
  // Si c'est un objet File
  if (photoPath instanceof File) {
    return URL.createObjectURL(photoPath)
  }
  
  // Si c'est déjà une URL complète
  if (typeof photoPath === 'string' && photoPath.startsWith('http')) {
    return photoPath
  }

  // Si c'est un chemin relatif commençant par /
  if (typeof photoPath === 'string' && photoPath.startsWith('/')) {
    // cas fréquent: "/data/uploads/xxx.jpg"
    return `${API_URL}${photoPath}`
  }

  // Si c'est un nom de fichier simple, construire l'URL uploads
  if (typeof photoPath === 'string' && photoPath.length > 0) {
    return `${uploadsBase}${photoPath}`
  }
  
  return require('@/assets/images/user-default.png')
}

const handlePhotoError = (event) => {
  photoError.value = true
  event.target.src = require('@/assets/images/user-default.png')
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'Non spécifiée'
  try {
    return format(parseISO(dateStr), 'dd MMMM yyyy', { locale: fr })
  } catch (e) {
    return dateStr
  }
}

const getDirectionName = () => {
  if (!agent.value) return 'Non spécifiée'
  
  // Champs plats possibles
  const flat =
    agent.value.direction_name ||
    agent.value.directionNom ||
    agent.value.direction_libelle ||
    agent.value.directionLibelle ||
    agent.value.directionLabel
  if (typeof flat === 'string' && flat.trim()) return flat

  // Si le backend retourne déjà la direction (string ou objet)
  if (typeof agent.value.direction === 'string' && agent.value.direction.trim()) return agent.value.direction
  if (agent.value.direction?.nom) return agent.value.direction.nom
  if (agent.value.direction?.libelle) return agent.value.direction.libelle
  if (agent.value.direction?.name) return agent.value.direction.name
  if (agent.value.direction?.label) return agent.value.direction.label
  
  return 'Non spécifiée'
}

const getFonctionName = () => {
  if (!agent.value) return 'Non spécifiée'
  
  // Champs plats possibles
  const flat =
    agent.value.fonction_name ||
    agent.value.fonctionNom ||
    agent.value.fonction_libelle ||
    agent.value.fonctionLibelle ||
    agent.value.fonctionLabel
  if (typeof flat === 'string' && flat.trim()) return flat

  // Si le backend retourne déjà la fonction (string ou objet)
  if (typeof agent.value.fonction === 'string' && agent.value.fonction.trim()) return agent.value.fonction
  if (agent.value.fonction?.libelle) return agent.value.fonction.libelle
  if (agent.value.fonction?.nom) return agent.value.fonction.nom
  if (agent.value.fonction?.description) return agent.value.fonction.description
  if (agent.value.fonction?.name) return agent.value.fonction.name
  if (agent.value.fonction?.label) return agent.value.fonction.label
  
  return 'Non spécifiée'
}

const getGradeName = () => {
  if (!agent.value) return 'Non spécifié'
  
  // Champs plats possibles
  const flat =
    agent.value.grade_name ||
    agent.value.gradeNom ||
    agent.value.grade_libelle ||
    agent.value.gradeLibelle ||
    agent.value.gradeLabel
  if (typeof flat === 'string' && flat.trim()) return flat

  // Si le backend retourne déjà le grade (string ou objet)
  if (typeof agent.value.grade === 'string' && agent.value.grade.trim()) return agent.value.grade
  if (agent.value.grade?.libelle) return agent.value.grade.libelle
  if (agent.value.grade?.nom) return agent.value.grade.nom
  if (agent.value.grade?.description) return agent.value.grade.description
  if (agent.value.grade?.name) return agent.value.grade.name
  if (agent.value.grade?.label) return agent.value.grade.label
  
  return 'Non spécifié'
}

const getEtatCivilLabel = (etatCivil) => {
  if (!etatCivil) return ''
  const map = {
    celibataire: 'Célibataire',
    marie: 'Marié(e)',
    divorce: 'Divorcé(e)',
    veuf: 'Veuf(ve)',
    pacse: 'Pacsé(e)',
    concubinage: 'Concubinage'
  }
  return map[String(etatCivil).toLowerCase()] || etatCivil
}

const getNomsEnfantsList = (a) => {
  if (!a) return []
  const n = a.noms_enfants
  if (Array.isArray(n)) return n.filter(Boolean)
  if (typeof n === 'string') {
    try {
      const parsed = JSON.parse(n)
      return Array.isArray(parsed) ? parsed.filter(Boolean) : (n.trim() ? [n.trim()] : [])
    } catch (_) {
      return n.split(',').map(s => s.trim()).filter(Boolean)
    }
  }
  return []
}

onMounted(() => {
  fetchAgentData()
})
</script>

<style scoped>
.personnel-info-view {
  min-height: 100vh;
  background: #f5f7fa;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px;
  padding-bottom: 40px;
  overflow-y: auto;
  box-sizing: border-box;
}

.personnel-content {
  background: white;
  border-radius: 12px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  margin: auto;
}

.photo-section {
  margin-bottom: 30px;
}

.agent-photo {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #e0e0e0;
}

.photo-placeholder {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  border: 3px solid #e0e0e0;
}

.photo-placeholder .material-icons {
  font-size: 80px;
  color: #999;
}

.info-section h1 {
  margin: 0 0 30px 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.info-list {
  text-align: left;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-item:last-child {
  border-bottom: none;
}

.info-block-title {
  font-size: 16px;
  font-weight: 600;
  color: #1976d2;
  margin-top: 20px;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e0e0e0;
}

.noms-enfants-list {
  margin: 4px 0 0 0;
  padding-left: 18px;
  list-style: disc;
  text-align: left;
}

.noms-enfants-list li {
  margin-bottom: 2px;
}

.info-label {
  font-size: 14px;
  color: #757575;
  font-weight: 500;
}

.info-value {
  font-size: 14px;
  color: #333;
  font-weight: 600;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e0e0e0;
  border-radius: 50%;
  border-top-color: #1976d2;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-container p {
  color: #666;
  font-size: 14px;
}

.error-message {
  padding: 40px 20px;
  text-align: center;
  color: #d32f2f;
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
}

.error-message .material-icons {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}

.error-message p {
  font-size: 16px;
  margin: 8px 0;
}

.error-details {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .personnel-content {
    padding: 30px 20px;
  }
  
  .agent-photo {
    width: 120px;
    height: 120px;
  }
  
  .photo-placeholder {
    width: 120px;
    height: 120px;
  }
  
  .info-section h1 {
    font-size: 20px;
  }
}
</style>
